/** @jsx jsx */
import { Stack } from "@eworkit/iris";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import sanitize from "sanitize-html";
import { jsx } from "theme-ui";

import { MetaTags } from "@/components/MetaTags";
import { fetchPublicProfileBySlug, fetchPublicProfileInfoBySlug } from "@/data/public-profiles";
import { useFetcher } from "@/hooks/http";
import { IWithRouteMatchProps } from "@/infrastructure/Route";
import { truncateToWholeWords } from "@/utils/strings";

import { ClientPageHeader } from "./ClientPageHeader";
import { ClientPageNavigationTabPanels } from "./components";
import { transformRouteProps } from "./selectors";

export interface ClientPageProps extends IWithRouteMatchProps {
  isPublic?: boolean;
}

export const ClientPage: FC<ClientPageProps> = ({ isPublic, ...restProps }) => {
  const { t } = useTranslation("public");
  const { slug } = transformRouteProps(restProps);

  const { payload } = useFetcher(fetchPublicProfileBySlug, { slug });
  const clientInfoFetcher = useFetcher(fetchPublicProfileInfoBySlug, { slug });

  if (!payload) {
    return null;
  }

  return (
    <React.Fragment>
      <MetaTags
        title={t("CLIENT_PAGE.META.TITLE", { name: truncateToWholeWords(payload.name, 51) })}
        description={truncateToWholeWords(sanitize(payload.aboutUs, { allowedTags: [], allowedAttributes: {} }), 51)}
        ogImage={payload.backgroundImage.publicUrl}
        ogImageType={payload.backgroundImage.contentType}
      />

      <Stack>
        <ClientPageHeader publicProfile={payload} info={clientInfoFetcher.payload} isPublic={isPublic} />
        <ClientPageNavigationTabPanels publicProfile={payload} info={clientInfoFetcher?.payload} isPublic={isPublic} />
      </Stack>
    </React.Fragment>
  );
};
